import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import {
  CircularProgress,
  Button,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Switch,
  Tooltip,
} from "@nextui-org/react";
import { BsDatabaseFill } from "react-icons/bs";
import { FiPlus, FiList, FiCalendar } from "react-icons/fi";
import {
  MdOutlineStarBorder,
  MdCheckCircleOutline,
  MdSchedule,
  MdEdit,
  MdOutlineArchive,
  MdOutlineUnarchive,
  MdDeleteOutline,
} from "react-icons/md";

import CreateDebriefModal from "./CreateDebriefModal";
import ConfirmModal from "./ConfirmModal";
import DebriefsCalendar from "./DebriefsCalendar";
import {
  isDebriefOverdue,
  isDebriefUpcoming,
  isOperatorActive,
  toDebriefDate,
} from "./debriefUtils";
import Modal from "../../components/Modal";
import GenericTable from "../../components/Kit/GenericTable";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import {
  fetchDebriefs,
  deleteDebrief,
  setDebriefArchived,
} from "../../redux/actions/debriefActions";
import getConfig from "../../conf/config";
import myAxios from "../../utils/axiosWrapper";
import { auth } from "../../conf/firebase";
import { ArrowTopRightIcon } from "../../assets/icons/audio-icons";
import { useOrganisations } from "../insights/useInsightsData";

const PLANNING_UPCOMING = "upcoming";
const PLANNING_OVERDUE = "overdue";

const toTimestamp = (value) => toDebriefDate(value)?.getTime() || 0;

const Debriefs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { debriefId } = useParams();

  const { currentUser } = auth;

  const { user } = useSelector((state) => state.user);
  const { debriefs, loading } = useSelector((state) => state.debrief?.debrief || {});
  const { departments } = useOrganisations();

  const [operators, setOperators] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [selectedOperatorFilter, setSelectedOperatorFilter] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState("");
  const [planningFilter, setPlanningFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createDefaultScheduledDate, setCreateDefaultScheduledDate] = useState("");
  const [operatorFilterSearch, setOperatorFilterSearch] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const isSelectingRef = useRef(false);

  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "date",
    direction: "desc",
  });

  const debriefsTableColumns = useMemo(
    () => [
      { key: "operator_name", label: t("Représentant"), size: 3, sortable: true },
      { key: "date", label: t("Date du debrief"), size: 2, sortable: true },
      { key: "scheduled_date", label: t("Prévu le"), size: 2, sortable: true },
      { key: "period", label: t("Période"), size: 2, sortable: false },
      { key: "status", label: t("Statut"), size: 2, sortable: true },
      { key: "average_grade", label: t("Note moy."), size: 2, sortable: true },
      { key: "total_calls", label: t("Appels"), size: 1, sortable: true },
      { key: "actions", label: "", size: 2, sortable: false, alignEnd: true },
    ],
    [t]
  );

  // Fetch operators for the filter dropdown
  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    const fetchOperators = async () => {
      setOperatorsLoading(true);
      try {
        const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/admin/operators`);
        const operatorMap = response?.data?.[0]?.operators || {};
        const formattedOperators = [];
        Object.keys(operatorMap).forEach((operatorId) => {
          if (operatorId === "no_operator_id") return;
          formattedOperators.push(operatorMap[operatorId]);
        });
        setOperators(formattedOperators);
      } catch (error) {
        console.error("Error fetching operators:", error);
      }
      setOperatorsLoading(false);
    };
    fetchOperators();
  }, [user]);

  const serverFilters = useMemo(() => {
    const filters = {};
    if (selectedOperatorFilter) filters.operator_id = selectedOperatorFilter;
    if (selectedStatusFilter) filters.status = selectedStatusFilter;
    if (showArchived) filters.archived = "only";
    return filters;
  }, [selectedOperatorFilter, selectedStatusFilter, showArchived]);

  const reloadDebriefs = useCallback(
    () => dispatch(fetchDebriefs(serverFilters)),
    [dispatch, serverFilters]
  );

  // Fetch debriefs (also re-fetches when returning from detail page, as debriefId becomes undefined)
  useEffect(() => {
    if (debriefId) return;
    reloadDebriefs();
  }, [reloadDebriefs, user, debriefId]);

  // Full operator list (inactive ones included) so existing debriefs keep their names.
  const operatorsById = useMemo(() => {
    const map = {};
    operators.forEach((op) => {
      map[op.id] = op;
    });
    return map;
  }, [operators]);

  const getOperatorName = useCallback(
    (operatorId) => {
      const operator = operatorsById[operatorId];
      return operator ? `${operator.first_name} ${operator.last_name}` : operatorId || "";
    },
    [operatorsById]
  );

  // Picker options: no easier admins, no deactivated operators, and only the
  // selected department when one is chosen.
  const nonAdminOperators = useMemo(
    () =>
      operators.filter(
        (op) =>
          !op?.user?.is_easier_admin &&
          isOperatorActive(op) &&
          (!selectedDepartmentFilter || op.department_id === selectedDepartmentFilter)
      ),
    [operators, selectedDepartmentFilter]
  );

  const filteredOperatorsForFilter = useMemo(() => {
    if (!operatorFilterSearch) return nonAdminOperators;
    const search = operatorFilterSearch.toLowerCase();
    return nonAdminOperators.filter(
      (op) =>
        op.first_name?.toLowerCase().includes(search) ||
        op.last_name?.toLowerCase().includes(search) ||
        op.email?.toLowerCase().includes(search)
    );
  }, [nonAdminOperators, operatorFilterSearch]);

  const sortedDepartments = useMemo(
    () => [...(departments || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [departments]
  );

  const handleDepartmentFilterChange = (departmentId) => {
    setSelectedDepartmentFilter(departmentId);
    // Drop an operator selection that no longer belongs to the department.
    const selectedOperator = operatorsById[selectedOperatorFilter];
    if (departmentId && selectedOperator && selectedOperator.department_id !== departmentId) {
      setSelectedOperatorFilter("");
    }
  };

  const handlePlanningFilterChange = (value) => {
    setPlanningFilter(value);
    if (value) {
      // Planning views read chronologically: soonest / longest overdue first.
      setSortDescriptor({ sortBy: "scheduled_date", direction: "asc" });
    }
  };

  // Client-side filters (department via the operator, planning state)
  const filteredDebriefs = useMemo(() => {
    if (!debriefs) return [];
    return debriefs.filter((debrief) => {
      if (selectedDepartmentFilter) {
        const operator = operatorsById[debrief.operator_id];
        if (operator?.department_id !== selectedDepartmentFilter) return false;
      }
      if (planningFilter === PLANNING_UPCOMING && !isDebriefUpcoming(debrief)) return false;
      if (planningFilter === PLANNING_OVERDUE && !isDebriefOverdue(debrief)) return false;
      return true;
    });
  }, [debriefs, selectedDepartmentFilter, planningFilter, operatorsById]);

  // Sort debriefs
  const sortedDebriefs = useMemo(() => {
    const { sortBy, direction } = sortDescriptor;

    // Debriefs without a scheduled date always go last, whatever the direction.
    if (sortBy === "scheduled_date") {
      return [...filteredDebriefs].sort((a, b) => {
        const aVal = toTimestamp(a.scheduled_date);
        const bVal = toTimestamp(b.scheduled_date);
        if (!aVal || !bVal) return (aVal ? 0 : 1) - (bVal ? 0 : 1);
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }

    return [...filteredDebriefs].sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortBy) {
        case "date":
          aVal = toTimestamp(a.date);
          bVal = toTimestamp(b.date);
          break;
        case "operator_name":
          aVal = getOperatorName(a.operator_id).toLowerCase();
          bVal = getOperatorName(b.operator_id).toLowerCase();
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "average_grade":
          aVal = a.kpi_snapshot?.average_grade || 0;
          bVal = b.kpi_snapshot?.average_grade || 0;
          break;
        case "total_calls":
          aVal = a.kpi_snapshot?.total_calls || 0;
          bVal = b.kpi_snapshot?.total_calls || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredDebriefs, sortDescriptor, getOperatorName]);

  // If debriefId is in the URL, render the detail page via Outlet
  if (debriefId) {
    return <Outlet />;
  }

  const hasActiveFilters =
    selectedOperatorFilter ||
    selectedStatusFilter ||
    selectedDepartmentFilter ||
    planningFilter ||
    showArchived;

  const clearFilters = () => {
    setSelectedOperatorFilter("");
    setSelectedStatusFilter("");
    setSelectedDepartmentFilter("");
    setPlanningFilter("");
    setShowArchived(false);
  };

  const handleRowClick = (item) => () => {
    navigate(`/debriefs/${item.id}`);
  };

  const openCreateModal = (scheduledDate = "") => {
    setCreateDefaultScheduledDate(scheduledDate);
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = (newDebriefId) => {
    setIsCreateModalOpen(false);
    reloadDebriefs();
    if (newDebriefId) {
      navigate(`/debriefs/${newDebriefId}`);
    }
  };

  const handleToggleArchive = async (item) => {
    try {
      await setDebriefArchived(item.id, !item.archived);
      reloadDebriefs();
    } catch (error) {
      console.error("Error archiving debrief:", error);
    }
  };

  const handleDelete = (item) => {
    setConfirmAction({
      message: t("Êtes-vous sûr de vouloir supprimer ce debrief ?"),
      confirmLabel: t("Supprimer"),
      destructive: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await deleteDebrief(item.id);
          reloadDebriefs();
        } catch (error) {
          console.error("Error deleting debrief:", error);
        }
      },
    });
  };

  const formatDate = (date) => {
    const d = toDebriefDate(date);
    if (!d) return "-";
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadge = (item) => {
    if (item.status === "completed") {
      return (
        <div className="px-2 py-1 bg-teal-500/10 rounded-lg inline-flex items-center gap-1">
          <MdCheckCircleOutline
            size={14}
            className="text-teal-500"
          />
          <span className="text-teal-500 text-xs font-semibold">{t("Terminé")}</span>
        </div>
      );
    }
    if (item.status === "scheduled") {
      if (isDebriefOverdue(item)) {
        return (
          <div className="px-2 py-1 bg-red-500/10 rounded-lg inline-flex items-center gap-1">
            <MdSchedule
              size={14}
              className="text-red-500"
            />
            <span className="text-red-500 text-xs font-semibold">{t("En retard")}</span>
          </div>
        );
      }
      return (
        <div className="px-2 py-1 bg-yellow-500/10 rounded-lg inline-flex items-center gap-1">
          <MdSchedule
            size={14}
            className="text-yellow-500"
          />
          <span className="text-yellow-500 text-xs font-semibold">{t("Planifié")}</span>
        </div>
      );
    }
    return (
      <div className="px-2 py-1 bg-gray-500/10 rounded-lg inline-flex items-center gap-1">
        <MdEdit
          size={14}
          className="text-gray-500"
        />
        <span className="text-gray-500 text-xs font-semibold">{t("Brouillon")}</span>
      </div>
    );
  };

  const getGradeBadge = (avgGrade) => {
    if (avgGrade < 0) return <span className="text-neutral-400 text-sm">-</span>;
    const normalized = Math.round((avgGrade + Number.EPSILON) * 10) / 10;
    return (
      <div className="px-2 py-1 bg-teal-500/10 rounded-lg inline-flex items-center gap-1">
        <MdOutlineStarBorder
          size={14}
          className="text-teal-500"
        />
        <span className="text-teal-500 text-xs font-semibold">{normalized}</span>
      </div>
    );
  };

  const rowIconButtonClass =
    "p-2 rounded-lg flex items-center justify-center text-neutral-500 transition-colors";

  const renderCell = (item, columnKey) => {
    const operatorInfo = operatorsById[item.operator_id];
    const avgGrade = item.kpi_snapshot?.average_grade || -1;

    switch (columnKey) {
      case "operator_name":
        return operatorInfo ? (
          <ACircleUserDisplay
            firstname={operatorInfo.first_name}
            lastname={operatorInfo.last_name}
            email={operatorInfo.email}
          />
        ) : (
          <span className="text-neutral-500 text-sm">{item.operator_id}</span>
        );

      case "date":
        return <span className="text-neutral-700/60 text-sm">{formatDate(item.date)}</span>;

      case "scheduled_date":
        return (
          <span
            className={`text-sm ${
              isDebriefOverdue(item) ? "text-red-500 font-medium" : "text-neutral-700/60"
            }`}
          >
            {formatDate(item.scheduled_date)}
          </span>
        );

      case "period":
        return (
          <span className="text-neutral-700/60 text-sm">
            {formatDate(item.period_start)} - {formatDate(item.period_end)}
          </span>
        );

      case "status":
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {getStatusBadge(item)}
            {item.archived && (
              <div className="px-2 py-1 bg-neutral-500/10 rounded-lg inline-flex items-center gap-1">
                <MdOutlineArchive
                  size={14}
                  className="text-neutral-500"
                />
                <span className="text-neutral-500 text-xs font-semibold">{t("Archivé")}</span>
              </div>
            )}
          </div>
        );

      case "average_grade":
        return getGradeBadge(avgGrade);

      case "total_calls":
        return (
          <span className="text-neutral-700/60 text-sm">{item.kpi_snapshot?.total_calls || 0}</span>
        );

      case "actions":
        return (
          <div className="flex justify-end items-center gap-1">
            <Tooltip content={item.archived ? t("Désarchiver") : t("Archiver")}>
              <button
                type="button"
                aria-label={item.archived ? t("Désarchiver") : t("Archiver")}
                className={`${rowIconButtonClass} hover:bg-neutral-100 hover:text-neutral-700`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleArchive(item);
                }}
              >
                {item.archived ? <MdOutlineUnarchive size={18} /> : <MdOutlineArchive size={18} />}
              </button>
            </Tooltip>
            <Tooltip content={t("Supprimer")}>
              <button
                type="button"
                aria-label={t("Supprimer")}
                className={`${rowIconButtonClass} hover:bg-red-50 hover:text-red-500`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(item);
                }}
              >
                <MdDeleteOutline size={18} />
              </button>
            </Tooltip>
            <button
              type="button"
              aria-label={t("Ouvrir le debrief")}
              className="p-2.5 bg-indigo-600 rounded-lg shadow-sm flex items-center justify-center hover:bg-indigo-700 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/debriefs/${item.id}`);
              }}
            >
              <ArrowTopRightIcon size={12} />
            </button>
          </div>
        );

      default:
        return <span>{item[columnKey]}</span>;
    }
  };

  const renderEmptyState = () => (
    <div className="w-full h-full flex-1 flex flex-col items-center justify-center bg-white rounded-3xl py-12">
      <BsDatabaseFill className="text-gray-300 w-12 h-12" />
      <h3 className="text-sm text-gray-400 mt-3">{t("Aucun debrief trouvé")}</h3>
      {!hasActiveFilters && (
        <Button
          color="primary"
          className="mt-4"
          startContent={<FiPlus />}
          onClick={() => openCreateModal()}
        >
          {t("Créer un premier debrief")}
        </Button>
      )}
    </div>
  );

  const renderLoading = () => (
    <div className="w-full h-full flex items-center justify-center py-12">
      <CircularProgress
        size="lg"
        aria-label="Loading..."
        color="primary"
      />
    </div>
  );

  const viewModeButtonClass = (mode) =>
    `px-3 h-10 flex items-center gap-2 text-sm font-medium transition-colors ${
      viewMode === mode ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"
    }`;

  const renderContent = () => {
    if (loading && !(viewMode === "calendar" && sortedDebriefs.length)) return renderLoading();
    if (viewMode === "calendar") {
      return (
        <DebriefsCalendar
          debriefs={sortedDebriefs}
          getOperatorName={getOperatorName}
          onOpenDebrief={(id) => navigate(`/debriefs/${id}`)}
          onCreateForDate={(dateKey) => openCreateModal(dateKey)}
        />
      );
    }
    if (sortedDebriefs.length === 0) return renderEmptyState();
    return (
      <GenericTable
        items={sortedDebriefs}
        titles={debriefsTableColumns}
        getItemContent={renderCell}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        onRowClick={handleRowClick}
      />
    );
  };

  return (
    <div className="flex flex-col h-full w-full p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="whitespace-nowrap text-[36px] font-semibold text-primary">
          {t("Debriefs")}
        </h1>
        <div className="flex items-center gap-3">
          <div
            className="flex rounded-lg overflow-hidden border border-neutral-200"
            role="group"
            aria-label={t("Affichage")}
          >
            <button
              type="button"
              className={viewModeButtonClass("list")}
              aria-pressed={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <FiList size={16} />
              {t("Liste")}
            </button>
            <button
              type="button"
              className={viewModeButtonClass("calendar")}
              aria-pressed={viewMode === "calendar"}
              onClick={() => setViewMode("calendar")}
            >
              <FiCalendar size={16} />
              {t("Calendrier")}
            </button>
          </div>
          <Button
            color="primary"
            startContent={<FiPlus />}
            onClick={() => openCreateModal()}
          >
            {t("Nouveau debrief")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {sortedDepartments.length > 0 && (
          <Select
            label={t("Département")}
            placeholder={t("Tous les départements")}
            className="max-w-[220px]"
            selectedKeys={selectedDepartmentFilter ? [selectedDepartmentFilter] : []}
            onChange={(e) => handleDepartmentFilterChange(e.target.value)}
            items={sortedDepartments}
          >
            {(department) => <SelectItem key={department.id}>{department.name}</SelectItem>}
          </Select>
        )}

        <Autocomplete
          label={t("Représentant")}
          placeholder={t("Tous les représentants")}
          className="max-w-xs"
          selectedKey={selectedOperatorFilter || null}
          onSelectionChange={(key) => {
            isSelectingRef.current = true;
            setSelectedOperatorFilter(key || "");
          }}
          onInputChange={(value) => {
            if (isSelectingRef.current) {
              isSelectingRef.current = false;
              return;
            }
            setOperatorFilterSearch(value);
          }}
          isLoading={operatorsLoading}
          isClearable
          items={filteredOperatorsForFilter}
        >
          {(op) => (
            <AutocompleteItem
              key={op.id}
              textValue={`${op.first_name} ${op.last_name}`}
            >
              <div className="flex flex-col">
                <span>{`${op.first_name} ${op.last_name}`}</span>
                {op.email && <span className="text-xs text-default-400">{op.email}</span>}
              </div>
            </AutocompleteItem>
          )}
        </Autocomplete>

        <Select
          label={t("Statut")}
          placeholder={t("Tous les statuts")}
          className="max-w-[200px]"
          selectedKeys={selectedStatusFilter ? [selectedStatusFilter] : []}
          onChange={(e) => setSelectedStatusFilter(e.target.value)}
        >
          <SelectItem
            key="draft"
            value="draft"
          >
            {t("Brouillon")}
          </SelectItem>
          <SelectItem
            key="completed"
            value="completed"
          >
            {t("Terminé")}
          </SelectItem>
          <SelectItem
            key="scheduled"
            value="scheduled"
          >
            {t("Planifié")}
          </SelectItem>
        </Select>

        <Select
          label={t("Planning")}
          placeholder={t("Tous les debriefs")}
          className="max-w-[200px]"
          selectedKeys={planningFilter ? [planningFilter] : []}
          onChange={(e) => handlePlanningFilterChange(e.target.value)}
        >
          <SelectItem
            key={PLANNING_UPCOMING}
            value={PLANNING_UPCOMING}
          >
            {t("À venir")}
          </SelectItem>
          <SelectItem
            key={PLANNING_OVERDUE}
            value={PLANNING_OVERDUE}
          >
            {t("En retard")}
          </SelectItem>
        </Select>

        <Switch
          size="sm"
          isSelected={showArchived}
          onValueChange={setShowArchived}
        >
          <span className="text-sm text-gray-700">{t("Archivés")}</span>
        </Switch>

        {hasActiveFilters && (
          <Button
            variant="light"
            color="primary"
            onClick={clearFilters}
          >
            {t("Effacer les filtres")}
          </Button>
        )}
      </div>

      {/* Table / calendar */}
      <div className="flex-1 bg-white rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden">
        {renderContent()}
      </div>

      {/* Create Debrief Modal */}
      {isCreateModalOpen && (
        <Modal
          isOpen={isCreateModalOpen}
          showFooter={false}
          size="2xl"
          handleOnClose={() => setIsCreateModalOpen(false)}
        >
          <CreateDebriefModal
            operators={operators}
            defaultScheduledDate={createDefaultScheduledDate}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={handleCreateSuccess}
          />
        </Modal>
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <ConfirmModal
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          destructive={confirmAction.destructive}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

export default Debriefs;
